import React, {useEffect, useState} from "react";
import {deepEqual} from "../../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import {OutlinedInput} from "../../../../../../../components/base/input/outlined_input";
import {Search} from "@mui/icons-material";
import {Col, Row} from "reactstrap";
import TryAgain from "../../../../../../../components/app-specific/try-again";
import Pagination from "../../../../../../../components/base/pagination";
import SentNotificationCard from "../../../../../../../components/app-specific/notification-cards/sent-notification-card";
import {Typography} from "@mui/material";
import {useHistory, useLocation} from "react-router-dom";
import useQuery from "../../../../../../../hooks/use-query";
import {stringifyUrl} from "query-string";

const initialPaginationInfo = {
    currentPage: 1,
    pageSize: 4,
    length: 0,
};

/**
 * Prepares the search request for search api with the given parameters
 * @param filters {any} the filters used for filtering
 * @param paginationInfo {any}
 * @return {{requestType, filters: {keyword: *}}}
 */
const prepareForApi = (filters, paginationInfo = null,) => {
    const res = {
        filters: {
            descriptionAndCategoryAndDataKeyword: filters?.keywords?.toString() ?? null,
            clientRecieverId: null,
            userReceiverId: null,
            messageTypeId: null,
            senderId: null,
            sentFromDate: null,
            sentToDate: null
        },
    };
    if (paginationInfo) {
        res['paginationInfo'] = paginationInfo;
    }
    return res;
}


const NotificationHistoryView = ({loading: loadingProp, setSelectedHistory}) => {
    const history = useHistory()
    const location = useLocation();
    const [query] = useQuery()
    const [loading, setLoading] = useState(true)
    const [paginationInfo, setPaginationInfo] = useState(null)
    const [keyword, setKeyword] = useState(null)
    const [notifications, setNotifications] = useState([])
    const isMounted = useIsMounted()

    /**
     * Listens for the changes in the query of the url and with each change:
     * Determines whether the filters or the pagination info has changed, and if so, calls the api for fetching the
     * data again.
     */
    useEffect(() => {
        if (loadingProp) return;
        let callApi = false
        let newPaginationInfo = query?.page ? JSON.parse(query.page) : null
        if (!newPaginationInfo) {
            newPaginationInfo = initialPaginationInfo
        }
        if (!deepEqual(paginationInfo, newPaginationInfo)) {
            setPaginationInfo(newPaginationInfo)
            callApi = true
        }
        const newKeywords = query?.keyword;
        if (!deepEqual(keyword, newKeywords)) {
            setKeyword(newKeywords)
            callApi = true
        }
        if (!callApi) return
        searchNotifications(prepareForApi({keywords: newKeywords}, newPaginationInfo))
    }, [query, loadingProp])


    /**
     * Searches the notifications based on the provided filters and sets the inner state notifications list and
     * pagination info if the result is successful.
     * @param {any} forApi the provided values for the api call.
     */
    const searchNotifications = (forApi) => {
        setLoading(true)
        Api.searchSentNotifications(forApi).then((response) => {
            if (!isMounted()) return
            if (response?.resultFlag) {
                setPaginationInfo(prevState => ({...prevState, length: response.data?.paginationInfo?.length ?? 0}))
                setNotifications(response.data?.resultList?.map((e, index) => ({
                    ...e,
                    id: `${keyword ?? ''}-${(response.data?.paginationInfo?.currentPage ?? 1) * initialPaginationInfo.pageSize + index}`
                })))
            }
            setLoading(false)
        })
    }

    /**
     * Removes the filters from the url to trigger a new api call with no filters.
     */
    const removeFilters = () => {
        history.replace(stringifyUrl({
            url: location.pathname,
            query: {}
        }))
    }

    /**
     * Searches for the notifications again with the same filters and pagination info.
     */
    const searchAgain = () => {
        searchNotifications(prepareForApi({keywords: keyword}, paginationInfo))
    }

    /**
     * Sets the selected notification history in parent's state so that the form section can be notified and duplicate
     * the values based on the changes.
     * @param {any} notification the notification data to be duplicated.
     */
    const duplicateNotification = (notification) => {
        setSelectedHistory(notification)
    }

    /**
     * Changes the current page of the pagination info on the url of the page so that the search function can be
     * triggered again.
     * @param e {any}
     */
    const onPageChange = (e) => {
        history.push(stringifyUrl({
            url: location.pathname,
            query: {...query, page: JSON.stringify({...paginationInfo, currentPage: e.selected + 1})},
        }))
    }

    /**
     * Changes the selected keyword of the searching filter to trigger a searching api call.
     */
    const search = (value = null) => {
        history.replace(stringifyUrl({
            url: location.pathname,
            query: {keyword: value ?? keyword}
        }))
    }

    /**
     * Searches for the input of user after they press the Enter key.
     * @param e
     */
    const onKeyPress = (e) => {
        // if enter is pressed
        if (e.key === 'Enter') {
            setKeyword(e.target.value)
            search(e.target.value);
        }
    }


    return (
        <>
            <Col xs={12} className='px-2'>
                <Typography variant={'h6'} fontWeight={600} marginBlock={2}>
                    History
                </Typography>
            </Col>
            <Col xs={12} className='px-2 mb-3'>
                <OutlinedInput
                    onKeyPress={onKeyPress}
                    onChange={(newValue) => setKeyword(newValue)}
                    type={'text'}
                    placeholder={'search for notifications'}
                    value={keyword ?? ''}
                    icon={<Search className={'icon normal'}/>}
                    onIconClick={() => search(keyword)}
                    iconButtonProps={{
                        disabled: loadingProp || loading
                    }}
                />
            </Col>
            {
                loading || loadingProp
                    ? <>
                        <div className={'d-flex flex-wrap w-100 justify-content-center'}>
                            {Array(2).fill(null).map((e, i) => (
                                <div key={i} className={'loading sent-notification-card'}>
                                    <div className={'w-100 h-100'}/>
                                </div>
                            ))}
                        </div>
                    </>
                    : notifications?.length === 0
                        ? query?.keyword?.length
                            ? <TryAgain
                                buttonText={"Remove Filters"}
                                onClick={removeFilters}
                                showButton={true}
                            />
                            : <TryAgain
                                buttonText={"You have not sent any notifications yet."}
                                onClick={searchAgain}
                            />
                        : notifications?.map(notification => (
                            <Col xs={12} className={'px-0'} key={notification?.id}>
                                <SentNotificationCard
                                    notification={notification}
                                    onDuplicate={() => duplicateNotification(notification)}
                                />
                            </Col>
                        ))
            }
            {
                !loading && !loadingProp && paginationInfo.length > paginationInfo.pageSize && (
                    <Row className='no-gutters w-100 justify-content-center align-items-center mb-5 mt-3'>
                        <Pagination
                            currentPage={paginationInfo.currentPage}
                            length={paginationInfo.length}
                            pageSize={paginationInfo.pageSize}
                            onPageChange={onPageChange}
                        />
                    </Row>
                )
            }
        </>
    )
}


export default NotificationHistoryView;
